import { z } from "zod";

/**
 * User Settings DTOs
 * Shared types between backend and frontend for user settings endpoints
 */

// ============= Common Schemas =============

/**
 * Valid setting keys (report pages that support saved filters)
 */
export enum SettingKey {
  CREW_COURSE_OVERVIEW = "report_filter.crew_course_overview",
  POSITION_OVERVIEW = "report_filter.position_overview",
  COMPANY_SELECTOR = "company_selector",
  AIRCRAFT_VIEW_MODE = "compliance.aircraft.view_mode",
  AIRCRAFT_OPERATION_VIEW_HINT = "hint.compliance.aircraft.operation_view",
  AIRCRAFT_OPERATION_COPY_HINT = "hint.aircraft.operation_copy",
  LIMITATION_DEFINITION_STRUCTURE_HINT = "hint.limitations.definition_structure",
  LIMITATION_ASSIGNMENT_HINT = "hint.limitations.assignment",
  LIMITATION_SETS_HINT = "hint.limitations.sets",
  AIRCRAFT_OPERATION_HISTORY_HINT = "hint.aircraft.operation_history",
  AIRCRAFT_OVERVIEW = "report_filter.aircraft_overview",
}

export const NEW_BADGE_EXPIRES_AT: Partial<Record<SettingKey, string>> = {};

export const SettingKeySchema = z.enum(SettingKey);

/**
 * Setting value schema - flexible JSON object
 * For report filters: contains filter values (arrays of IDs, booleans, etc.)
 */
export const SettingValueSchema = z.record(z.string(), z.any());

// ============= Request Schemas =============

/**
 * Path params for getting settings
 */
export const GetSettingsParamSchema = z.object({
  key: SettingKeySchema,
});

/**
 * Query params for getting settings
 */
export const GetSettingsQuerySchema = z.object({
  crewId: z.coerce.number().int().positive().nullable().optional(),
});

/**
 * Body for saving default filter settings
 */
export const SaveDefaultSettingsBodySchema = z.object({
  crewId: z.number().int().positive().nullable(),
  value: SettingValueSchema,
});

/**
 * Body for updating a filter setting
 */
export const UpdateSettingBodySchema = z.object({
  name: z.string().min(1).max(191).optional(),
  value: SettingValueSchema.optional(),
});

/**
 * Path params for setting operations
 */
export const SettingIdParamSchema = z.object({
  settingId: z.coerce.number().int().positive(),
});

// ============= Response Schemas =============

/**
 * Single user setting response
 */
export const UserSettingSchema = z.object({
  id: z.number().int().positive(),
  userId: z.number().int().positive(),
  crewId: z.number().int().positive().nullable(),
  key: SettingKeySchema,
  name: z.string().nullable(),
  value: SettingValueSchema,
  isDefault: z.boolean(),
});

/**
 * Response for get default settings (can be null if no default saved)
 */
export const DefaultSettingResponseSchema = UserSettingSchema.nullable();

// ============= Type Exports =============

export type SettingValue = z.infer<typeof SettingValueSchema>;

export type GetSettingsParam = z.infer<typeof GetSettingsParamSchema>;
export type GetSettingsQuery = z.infer<typeof GetSettingsQuerySchema>;
export type SaveDefaultSettingsBody = z.infer<typeof SaveDefaultSettingsBodySchema>;
export type UpdateSettingBody = z.infer<typeof UpdateSettingBodySchema>;
export type SettingIdParam = z.infer<typeof SettingIdParamSchema>;

export type UserSettingDto = z.infer<typeof UserSettingSchema>;
export type DefaultSettingResponse = z.infer<typeof DefaultSettingResponseSchema>;

// ============= Cleanup Endpoint =============

export const CleanupSettingsByPrefixQuerySchema = z.object({
  prefix: z.string().min(1).max(64),
});

export const CleanupSettingsByPrefixResponseSchema = z.object({ deleted: z.number() });

export type CleanupSettingsByPrefixQuery = z.infer<typeof CleanupSettingsByPrefixQuerySchema>;
export type CleanupSettingsByPrefixResponse = z.infer<typeof CleanupSettingsByPrefixResponseSchema>;
